/**
 * Alert notices: Web Push and email from Teslyn Cloud (Diurnal parity C1: 03-038, 06-056, 02-017;
 * docs/specs/cloud/alerts.md). The kinds of notice, who receives each by default, what an event says, the events the
 * cloud tracker poller raises for SOS and lost tracking, the push payload and the email.
 *
 * - Kinds are a list, not a database enum: a new source (cloud diurnal QC in C3, the space weather poller in C6) adds
 *   its kinds here and raises events through `alert_raise`; preferences and the Alerts page follow the list. Decision
 *   692: "Storm watch" and "Storm now", never "Storm over".
 * - Default recipients (decision 53): SOS and lost tracking to the field crew (operators, not pilots) and managers on
 *   the crew of the aircraft's project; basemag alerts to the field crew. Crew dates decide (decision 919, R153): an
 *   operator whose crew row has ended is on break and receives no default notice, SOS included. When nobody would
 *   receive an event by default (an aircraft on no project, a project with no crew in force that day) the
 *   organisation's managers receive it instead, never all its field crew, and with no manager its tenant admins, so
 *   an SOS still reaches someone. Each person may switch a channel on or off for a kind ("Always" reaches them on
 *   break too); clients never receive these (Q37, decision 64: SOS and lost tracking are staff only).
 * - Once per event: an event's key names its episode (`<kind>:<provider>:<account>:<device>:<time>`), and the
 *   database keeps one event per key (`alert_event_key`). An episode that started more than 2 hours before the cloud
 *   noticed it is not raised (decision 445, `ALERT_QUEUE_MAX_AGE_MS`), as in Teslyn Field's queue (03-037).
 * - Text is plain text; the email's HTML is escaped (Diurnal inventory 02 AC-2.7).
 *
 * The Edge Functions use this module through tools/dev/copy-edge-shared.mjs (supabase/functions/_shared/domain/), so
 * every exported type is declared here and nothing imports a type from another module.
 */
import { maxFlightHours } from './aircraft.js';
import { crewOnDate } from './crew-dates.js';
import { appEmailFrom } from './email-sender.js';
import { flightStatus, SPEED_DIP_MS } from './flight-status.js';
import { projectUtcOffsetH } from './settings.js';
import { parseTime, utcToLocalMs } from './time-model.js';
import { ALERT_QUEUE_MAX_AGE_MS, TRACKER_SOURCE_NAME } from './tracker.js';

// ------------------------------------------------------------------------------------------------ kinds

export type NoticeKindId =
  | 'sos'
  | 'lost_tracking'
  | 'tracking_resumed'
  | 'flight_started'
  | 'flight_ended'
  | 'flight_overdue'
  | 'flight_landing_soon'
  | 'diurnal_exceedance'
  | 'diurnal_bad_data'
  | 'diurnal_gap'
  | 'diurnal_offline'
  | 'diurnal_online'
  | 'station_clock'
  | 'station_time_zone'
  | 'storm_watch'
  | 'storm_now'
  | 'crystal_pack'
  | 'approval_decided';
export type NoticeGroupId = 'tracking' | 'diurnal' | 'space_weather' | 'spectrometer' | 'client';
export type NoticeChannel = 'push' | 'email';
export type NoticeLevel = 'normal' | 'urgent' | 'critical';
/** Staff register roles on a project's crew (`project_crew.role`). */
export type NoticeCrewRole = 'pilot' | 'operator' | 'office_staff' | 'processor' | 'manager';
/** Teslyn Cloud staff roles (`membership.role`); clients have none of these. */
export type NoticeTenantRole = 'tenant_admin' | 'manager' | 'field_crew' | 'office_staff' | 'data_processor';

export interface NoticeKind {
  readonly id: NoticeKindId;
  readonly group: NoticeGroupId;
  /** The name on the Alerts page and in a notice's first words. */
  readonly label: string;
  /** When it is raised, in a sentence. */
  readonly description: string;
  /** Who receives it by default: these roles on the crew of the event's projects (decision 53). */
  readonly crewRoles: readonly NoticeCrewRole[];
  /**
   * Who receives it when nobody on a crew would: these staff roles of the organisation (decision 919: managers, never
   * the field crew, who may be at home on break); with nobody in them, the tenant admins (`NOTICE_LAST_RESORT`).
   */
  readonly fallbackRoles: readonly NoticeTenantRole[];
  /** The channels on by default for those people. */
  readonly defaults: { readonly push: boolean; readonly email: boolean };
  readonly level: NoticeLevel;
  /** Web Push `Urgency` (RFC 8030 §5.3): high wakes a sleeping phone. */
  readonly urgency: 'normal' | 'high';
  /** Web Push `TTL`: a notice older than this is not worth showing. */
  readonly ttlSeconds: number;
  /** Raised by a source today. False: listed (and testable) now, raised once its source is built. */
  readonly live: boolean;
  /** Not live: the sentence the Alerts page shows under the description, in plain words people may read. */
  readonly arrives: string | null;
  /**
   * Never sent to a pilot, whatever their own choice (decision 811, storms): not to anyone on the project's crew as a
   * pilot that day, and not to anyone off the crew whom the staff register lists as a pilot (the fallback, "Always").
   */
  readonly neverPilots?: true;
  /**
   * Acknowledge only (R140, MB 2 Nov 2026: "Flight started and Flight ended need no Resolve: acknowledging is
   * enough"; decision 941 Tracking resumed; MB 3 Nov 2026 Magnetic exceedances: "should not be a resolve only an
   * acknowledge, you can resolve space weather"): the event waits to be acknowledged like any other and is never
   * resolved. Acknowledging is its final state (`canResolveNotice`); an acknowledged one leaves "Needs action" for
   * the "Acknowledged" filter (R141). Says nothing about how the phone shows it: that is `nativeChannel`.
   */
  readonly ackOnly?: true;
  /**
   * The phone's notification channel when it is not the level's own (`noticeNativeMessage`): `flights` for the
   * flight and tracking-resumed news, which a person can quieten apart from the alerts (F27). An acknowledge-only
   * QC warning such as a magnetic exceedance has no entry here: it keeps the alerts channel and its own level.
   */
  readonly nativeChannel?: 'flights';
  /**
   * The flight's own operators (the flight entry's operator slots, `alert_audience` `flight_operators`) take the
   * place of the crew's operators in the default audience (R180, R181: "the flight's operator"); with none named who
   * has a sign-in, the operators on the project's crew as for the other kinds. A named operator is working that
   * flight, so their crew dates do not leave them out.
   */
  readonly flightOperators?: true;
  /**
   * Informational: with nobody in the crew roles or the fallback roles, nobody is told (no tenant admins as the last
   * resort, `NOTICE_LAST_RESORT`, which is there so an emergency always reaches someone).
   */
  readonly noLastResort?: true;
  /**
   * With no manager on the project's crew that day, the organisation's managers receive it as well as the crew roles
   * (R180: "alert operator and managers"; an overdue aircraft is news for the managers whether or not one is on the
   * crew). Pilots are still left out by `neverPilots`.
   */
  readonly managersWithoutCrew?: true;
  /**
   * Resolved by Teslyn itself when the problem is seen to be over (R180: an overdue flight once its landing is seen),
   * with `resolved_by` empty and the reason in the note; a person may still resolve it first.
   */
  readonly resolvedByTeslyn?: true;
  /**
   * One person only: the sign-in the event names (`alert_audience` `to_user`, the event's `detail.to_user_id`), if
   * they are staff of the organisation; no crew, no fallback and no last resort (R191's approval decided: to the staff
   * member who sent the request). Others receive it only by choosing "Always".
   */
  readonly toUser?: true;
  /** The test notice's words. */
  readonly test: { readonly title: string; readonly body: string };
}

const TRACKING_CREW: readonly NoticeCrewRole[] = ['operator', 'manager'];
/** Decision 919 (Q817 A): with no one on the crew, managers rather than all field crew (decisions 53 and 870 before). */
const TRACKING_FALLBACK: readonly NoticeTenantRole[] = ['manager'];
/** Decision 870: operators and managers on the crew, never pilots (no distraction while flying). */
const FLIGHT_CREW: readonly NoticeCrewRole[] = ['operator', 'manager'];
/** R180: the flight's operators (`flightOperators`) and the managers on the project's crew. */
const FLIGHT_WATCH_CREW: readonly NoticeCrewRole[] = ['operator', 'manager'];
const DIURNAL_CREW: readonly NoticeCrewRole[] = ['operator'];
const DIURNAL_FALLBACK: readonly NoticeTenantRole[] = ['manager'];

/** Every kind, in the order of the Alerts page. */
export const NOTICE_KINDS: readonly NoticeKind[] = [
  {
    id: 'sos',
    group: 'tracking',
    label: 'SOS',
    description: 'An aircraft’s tracker sends SOS (V2Track and Spider Tracks).',
    crewRoles: TRACKING_CREW,
    fallbackRoles: TRACKING_FALLBACK,
    defaults: { push: true, email: true },
    level: 'critical',
    urgency: 'high',
    ttlSeconds: 3600,
    live: true,
    arrives: null,
    test: {
      title: 'Test: SOS: VH-TST',
      body: 'This is a test. A real notice names the aircraft, its tracker and its last position.',
    },
  },
  {
    id: 'lost_tracking',
    group: 'tracking',
    label: 'Lost tracking',
    description:
      'An aircraft was airborne at its last position and has sent none for the lost-tracking minutes of Tracking settings (15 by default).',
    crewRoles: TRACKING_CREW,
    fallbackRoles: TRACKING_FALLBACK,
    defaults: { push: true, email: true },
    level: 'critical',
    urgency: 'high',
    ttlSeconds: 3600,
    live: true,
    arrives: null,
    test: {
      title: 'Test: Lost tracking: VH-TST',
      body: 'This is a test. A real notice says how long the aircraft has been quiet and where it was last.',
    },
  },
  {
    id: 'tracking_resumed',
    group: 'tracking',
    label: 'Tracking resumed',
    description: 'An aircraft reports again after a lost-tracking notice.',
    crewRoles: TRACKING_CREW,
    fallbackRoles: TRACKING_FALLBACK,
    defaults: { push: true, email: false },
    level: 'normal',
    urgency: 'normal',
    ttlSeconds: 3600,
    // decision 941 (Q839 B): good news needs no Resolve, acknowledging is enough, as Flight started and ended
    ackOnly: true,
    nativeChannel: 'flights',
    live: true,
    arrives: null,
    test: {
      title: 'Test: Tracking resumed: VH-TST',
      body: 'This is a test. A real notice says the aircraft is reporting again.',
    },
  },
  {
    // F27 (batch 7, Teslyn Mobile): the cloud tracker poller sees one of our aircraft take off (`flightNotices`)
    id: 'flight_started',
    group: 'tracking',
    label: 'Flight started',
    description:
      'One of our aircraft takes off: its tracker’s speed crosses the take-off speed (40 knots, helicopters 20 knots). Once per flight.',
    crewRoles: FLIGHT_CREW,
    fallbackRoles: TRACKING_FALLBACK,
    // decision 870: never to a pilot, on the crew or (with no crew: managers, decision 919) in the staff register
    neverPilots: true,
    defaults: { push: true, email: false },
    level: 'normal',
    urgency: 'normal',
    ttlSeconds: 2 * 3600,
    ackOnly: true,
    nativeChannel: 'flights',
    live: true,
    arrives: null,
    test: {
      title: 'Test: Flight started: VH-TST',
      body: 'This is a test. A real notice names the aircraft, its take-off time and its tracker.',
    },
  },
  {
    // F27: the same aircraft has stayed on the ground for 2 minutes after the flight (`flightNotices`)
    id: 'flight_ended',
    group: 'tracking',
    label: 'Flight ended',
    description:
      'One of our aircraft lands and stays below the take-off speed for 2 minutes. Once per flight, with the flight time.',
    crewRoles: FLIGHT_CREW,
    fallbackRoles: TRACKING_FALLBACK,
    // decision 870: never to a pilot, on the crew or (with no crew: managers, decision 919) in the staff register
    neverPilots: true,
    defaults: { push: true, email: false },
    level: 'normal',
    urgency: 'normal',
    ttlSeconds: 2 * 3600,
    ackOnly: true,
    nativeChannel: 'flights',
    live: true,
    arrives: null,
    test: {
      title: 'Test: Flight ended: VH-TST',
      body: 'This is a test. A real notice names the aircraft, its landing time and the flight time.',
    },
  },
  {
    // R180 (MB 3 Nov 2026): the tracker poller's flight watch (`flightWatchNotices`) over the flight entries with a
    // planned flight time, the field the phone's landing ETA uses (decision 986)
    id: 'flight_overdue',
    group: 'tracking',
    label: 'Overdue flights',
    description:
      'A flight has not landed 15 minutes after its landing ETA (take-off plus the planned flight time), or, with no planned time, by its aircraft’s endurance less the 45 min reserve: no landing from its tracker, no flight time on the phone and not finished in Teslyn Field. Critical once the aircraft reaches its maximum endurance. To the flight’s operators and the project’s managers (the organisation’s managers when none is on the crew); never to pilots. Teslyn resolves it when the landing is seen.',
    crewRoles: FLIGHT_WATCH_CREW,
    fallbackRoles: TRACKING_FALLBACK,
    flightOperators: true,
    managersWithoutCrew: true,
    neverPilots: true,
    defaults: { push: true, email: false },
    level: 'urgent',
    urgency: 'high',
    ttlSeconds: 3600,
    resolvedByTeslyn: true,
    live: true,
    arrives: null,
    test: {
      title: 'Test: Overdue flight: VH-TST',
      body: 'This is a test. A real notice names the aircraft and flight, its landing ETA and what its tracker last said.',
    },
  },
  {
    // R181 (MB 3 Nov 2026): the same flight watch, 30 minutes before the landing ETA, once per flight
    id: 'flight_landing_soon',
    group: 'tracking',
    label: 'Landing in 30 minutes',
    description:
      'A flight is 30 minutes from its landing ETA (with no planned flight time, from its aircraft’s endurance less the 45 min reserve) and has not landed. Once per flight, to the flight’s operators; none for a time under 30 minutes. Never to pilots.',
    crewRoles: ['operator'],
    fallbackRoles: [],
    flightOperators: true,
    noLastResort: true,
    neverPilots: true,
    defaults: { push: true, email: false },
    level: 'normal',
    urgency: 'high',
    ttlSeconds: 1800,
    // informational, as Flight started and Flight ended: acknowledged, never resolved
    ackOnly: true,
    nativeChannel: 'flights',
    live: true,
    arrives: null,
    test: {
      title: 'Test: Landing in 30 min: VH-TST',
      body: 'This is a test. A real notice names the aircraft and flight and its landing ETA.',
    },
  },
  {
    id: 'diurnal_exceedance',
    group: 'diurnal',
    label: 'Magnetic exceedances',
    description: 'A base station’s readings go over the project’s diurnal limits.',
    crewRoles: DIURNAL_CREW,
    fallbackRoles: DIURNAL_FALLBACK,
    defaults: { push: true, email: false },
    level: 'urgent',
    urgency: 'high',
    ttlSeconds: 6 * 3600,
    // MB 3 Nov 2026: "the alerts for mag exceedance should not be a resolve only an acknowledge, you can resolve
    // space weather". Acknowledging finishes it; the push keeps the alerts channel and the urgent level (no
    // `nativeChannel`). The other diurnal kinds keep Resolve.
    ackOnly: true,
    // parity C3: raised by cloud diurnal QC (diurnal-cloud.ts `cloudQcNotices`)
    live: true,
    arrives: null,
    test: {
      title: 'Test: Magnetic exceedance: station 22',
      body: 'This is a test. A real notice names the station, the limit and the peak.',
    },
  },
  {
    id: 'diurnal_bad_data',
    group: 'diurnal',
    label: 'Bad data',
    description: 'A base station sends readings marked bad.',
    crewRoles: DIURNAL_CREW,
    fallbackRoles: DIURNAL_FALLBACK,
    defaults: { push: true, email: false },
    level: 'normal',
    urgency: 'normal',
    ttlSeconds: 6 * 3600,
    // parity C3: raised by cloud diurnal QC (diurnal-cloud.ts `cloudQcNotices`)
    live: true,
    arrives: null,
    test: {
      title: 'Test: Bad data: station 15',
      body: 'This is a test. A real notice says how many readings were bad and when.',
    },
  },
  {
    id: 'diurnal_gap',
    group: 'diurnal',
    label: 'Data gaps',
    description: 'A base station’s readings stop for longer than its rate allows.',
    crewRoles: DIURNAL_CREW,
    fallbackRoles: DIURNAL_FALLBACK,
    defaults: { push: true, email: false },
    level: 'normal',
    urgency: 'normal',
    ttlSeconds: 6 * 3600,
    // parity C3: raised by cloud diurnal QC (diurnal-cloud.ts `cloudQcNotices`)
    live: true,
    arrives: null,
    test: {
      title: 'Test: Data gap: station 8',
      body: 'This is a test. A real notice gives the gap’s start and length.',
    },
  },
  {
    id: 'diurnal_offline',
    group: 'diurnal',
    label: 'Offline stations',
    description:
      'A TA Live base station with “Offline alerts” turned on (Station health) has sent nothing for the online threshold (15 minutes by default).',
    crewRoles: DIURNAL_CREW,
    fallbackRoles: DIURNAL_FALLBACK,
    defaults: { push: true, email: false },
    level: 'urgent',
    urgency: 'high',
    ttlSeconds: 6 * 3600,
    // parity C2: raised by the station-health poller (station-health.ts `stationNotices`)
    live: true,
    arrives: null,
    test: {
      title: 'Test: Station offline: USR Unit 3 (23)',
      body: 'This is a test. A real notice says when the station last sent a reading and how long it has been quiet.',
    },
  },
  {
    id: 'diurnal_online',
    group: 'diurnal',
    label: 'Stations back online',
    description: 'A base station sends again after an offline notice.',
    crewRoles: DIURNAL_CREW,
    fallbackRoles: DIURNAL_FALLBACK,
    defaults: { push: true, email: false },
    level: 'normal',
    urgency: 'normal',
    ttlSeconds: 6 * 3600,
    live: true,
    arrives: null,
    test: {
      title: 'Test: Station back online: USR Unit 3 (23)',
      body: 'This is a test. A real notice says the station is sending again and how long it was quiet.',
    },
  },
  {
    // decision 777: raised by the station-health poller (station-health.ts `stationClockNotices`)
    id: 'station_clock',
    group: 'diurnal',
    label: 'Station clocks',
    description:
      'A TA Live base station’s clock is more than 2 minutes out, after its usual network delay is taken off.',
    crewRoles: DIURNAL_CREW,
    fallbackRoles: DIURNAL_FALLBACK,
    defaults: { push: true, email: false },
    level: 'urgent',
    urgency: 'high',
    ttlSeconds: 12 * 3600,
    live: true,
    arrives: null,
    test: {
      title: 'Test: Station clock: USR Unit 3 (23)',
      body: 'This is a test. A real notice says how far the station’s clock is out and what to check.',
    },
  },
  {
    id: 'station_time_zone',
    group: 'diurnal',
    label: 'Station time zones',
    description: 'A TA Live base station’s clock looks set to another time zone than the day before.',
    crewRoles: DIURNAL_CREW,
    fallbackRoles: DIURNAL_FALLBACK,
    defaults: { push: true, email: false },
    level: 'urgent',
    urgency: 'high',
    ttlSeconds: 12 * 3600,
    live: true,
    arrives: null,
    test: {
      title: 'Test: Station time zone: USR Unit 3 (23)',
      body: 'This is a test. A real notice names the zone the clock now looks set to and the zone of the day before.',
    },
  },
  {
    id: 'storm_watch',
    group: 'space_weather',
    label: 'Storm watch',
    description:
      'A geomagnetic storm is forecast for a Mobilising or In progress project: a BoM geomagnetic warning, or a NOAA G1 or higher watch for the next 48 hours. One alert per storm, again if the forecast gets stronger. Never to pilots.',
    crewRoles: TRACKING_CREW,
    fallbackRoles: ['manager'],
    defaults: { push: true, email: false },
    level: 'normal',
    urgency: 'normal',
    ttlSeconds: 12 * 3600,
    // R115: Teslyn Cloud's own space weather check (space-weather-check.ts, every 15 minutes)
    neverPilots: true,
    live: true,
    arrives: null,
    test: {
      title: 'Test: Storm watch',
      body: 'This is a test. A real notice names the project, the warning and when it applies.',
    },
  },
  {
    id: 'storm_now',
    group: 'space_weather',
    label: 'Storm now',
    description:
      'A geomagnetic storm is under way at a Mobilising or In progress project’s Storm level: a BoM magnetic alert or the Australian K index at G1 or K 5 for tight limits, G2 or K 6 for 10 nT in 10 minutes. Needs the organisation’s BoM key. One alert per storm. Never to pilots.',
    crewRoles: TRACKING_CREW,
    fallbackRoles: ['manager'],
    defaults: { push: true, email: true },
    level: 'urgent',
    urgency: 'high',
    ttlSeconds: 6 * 3600,
    neverPilots: true,
    live: true,
    arrives: null,
    test: {
      title: 'Test: Storm now',
      body: 'This is a test. A real notice says magnetic data flown now will very likely fail.',
    },
  },
  {
    // R123, decision 830: raised by Teslyn Field's crystal pack check (crystal-pack.ts) through alert_raise_field
    id: 'crystal_pack',
    group: 'spectrometer',
    label: 'Crystal packs inconsistent',
    description:
      'A day’s ground cals read inconsistently: a later ground cal’s Th − Bk or resolution (FWHM) moved more than the project’s crystal pack limits from the day’s first (2 % and 0.3 points by default). Raised by Teslyn Field when it checks the cals of an imported flight; once per aircraft and day.',
    crewRoles: ['operator', 'processor', 'manager'],
    // decision 919: the crew roles of decision 852 less the operators, who may be on break
    fallbackRoles: ['data_processor', 'manager'],
    defaults: { push: true, email: true },
    level: 'urgent',
    urgency: 'normal',
    ttlSeconds: 12 * 3600,
    live: true,
    arrives: null,
    test: {
      title: 'Test: Crystal packs inconsistent: VH-TST',
      body: 'This is a test. A real notice names the aircraft and the day, and says how far the ground cals moved.',
    },
  },
  {
    // R191 and R190 (decision 1010): the client approved, declined or signed what a staff member sent for approval;
    // raised by the database from the audit rows approval.approved, approval.declined and approval.signed
    id: 'approval_decided',
    group: 'client',
    label: 'Client approval decided',
    description:
      'The client approved or declined a survey block change, the JSA, the JSP or the risk assessment you sent, or signed or declined a contract. To the person who sent it.',
    crewRoles: [],
    fallbackRoles: [],
    toUser: true,
    noLastResort: true,
    defaults: { push: true, email: true },
    level: 'normal',
    urgency: 'normal',
    ttlSeconds: 24 * 3600,
    // news: acknowledged, never resolved
    ackOnly: true,
    live: true,
    arrives: null,
    test: {
      title: 'Test: F00000: Survey block change approved',
      body: 'This is a test. A real notice names the project, what was sent, who decided and, for a decline, why.',
    },
  },
];

export const NOTICE_GROUP_LABEL: Readonly<Record<NoticeGroupId, string>> = {
  tracking: 'Aircraft tracking',
  diurnal: 'Diurnal QC',
  space_weather: 'Space weather',
  spectrometer: 'Spectrometer QC',
  client: 'Client approvals',
};

const BY_ID = new Map<string, NoticeKind>(NOTICE_KINDS.map((k) => [k.id, k]));

export function noticeKind(id: string): NoticeKind | null {
  return BY_ID.get(id) ?? null;
}

export function isNoticeKind(id: string): id is NoticeKindId {
  return BY_ID.has(id);
}

const CREW_ROLE_WORD: Readonly<Record<NoticeCrewRole, string>> = {
  pilot: 'pilots',
  operator: 'operators',
  office_staff: 'office staff',
  processor: 'processors',
  manager: 'managers',
};

const andList = (words: readonly string[]): string =>
  words.length <= 1 ? (words[0] ?? '') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;

/** "On for operators and managers on the project's crew", or "Off": what "Default" means for one channel. */
export function noticeDefaultText(kind: NoticeKind, channel: NoticeChannel): string {
  if (!kind.defaults[channel]) return 'Off';
  return `On for ${andList(kind.crewRoles.map((r) => CREW_ROLE_WORD[r]))} on the project’s crew`;
}

/**
 * The same in a few words, for the "Default" choice itself on the Alerts page (its full meaning is the choice's
 * title): "on for the crew" or "off".
 */
export function noticeDefaultShort(kind: NoticeKind, channel: NoticeChannel): string {
  return kind.defaults[channel] ? 'on for the crew' : 'off';
}

// ------------------------------------------------------------------------------------------------ events

/** One event to raise (`alert_raise`): the database keeps one per `event_key`. */
export interface NoticeEvent {
  readonly event_key: string;
  readonly kind: NoticeKindId;
  readonly title: string;
  readonly body: string;
  /** When it happened (UTC ISO): the fix an aircraft went quiet after, or when SOS was seen. */
  readonly occurred_at: string;
  /** The project it is about (diurnal, space weather); null for an aircraft (its projects come from assignments). */
  readonly project_id: string | null;
  /** The tracker asset it is about, when the device has one. */
  readonly asset_id: string | null;
  /** Raised only when the event with this key was raised (a resumed notice follows its lost-tracking notice). */
  readonly follows_key: string | null;
  readonly detail: Readonly<Record<string, string | number | boolean | null>>;
}

export const NOTICE_TITLE_MAX = 120;
export const NOTICE_BODY_MAX = 600;
const clip = (s: string, n: number): string => (s.length <= n ? s : `${s.slice(0, n - 1)}…`);

/** `2026-10-20 05:40:50 UTC`. Notices say UTC; the phone shows its own time beside a push notice. */
export function noticeTime(ms: number): string {
  return `${new Date(ms).toISOString().slice(0, 19).replace('T', ' ')} UTC`;
}

/** `25.1234° S, 118.4567° E, 1500 ft`. */
export function noticePosition(lat: number, lon: number, altFt: number | null | undefined): string {
  const ns = `${Math.abs(lat).toFixed(4)}° ${lat < 0 ? 'S' : 'N'}`;
  const ew = `${Math.abs(lon).toFixed(4)}° ${lon < 0 ? 'W' : 'E'}`;
  const alt =
    altFt === null || altFt === undefined || !Number.isFinite(altFt) ? '' : `, ${Math.round(altFt)} ft`;
  return `${ns}, ${ew}${alt}`;
}

/** Lost tracking of one device, as `tracker-poll.ts` `AlertRow` gives it. */
export interface NoticeAlertRow {
  readonly source_id: string;
  readonly asset_id: string | null;
  /** null: SOS not known this poll (the stored value stands). */
  readonly sos_active: boolean | null;
  readonly lost_since: string | null;
}

/** The device's stored `tracker_alert_state` before this poll. */
export interface NoticeStateRow {
  readonly device_id: string;
  readonly sos_active: boolean;
  readonly lost_since: string | null;
  readonly updated_at: string;
}

/** A fix of the device (this poll's position rows and the stored latest fixes). */
export interface NoticeFix {
  readonly source_id: string;
  readonly fix_time: string;
  readonly label?: string | null;
  readonly lat?: number | null;
  readonly lon?: number | null;
  readonly alt_ft?: number | null;
}

/** The tenant's tracker asset of a device (`tracker_asset`). */
export interface NoticeAsset {
  readonly id: string;
  readonly provider: string;
  readonly account_label: string;
  readonly device_id: string;
  readonly label: string;
  readonly registration: string;
  readonly hidden: boolean;
  readonly deleted_at: string | null;
}

export interface TrackerNoticeInput {
  readonly provider: string;
  /** The integration's label ('' for the only account of that provider). */
  readonly accountLabel: string;
  readonly alerts: readonly NoticeAlertRow[];
  readonly previous: readonly NoticeStateRow[];
  readonly fixes: readonly NoticeFix[];
  readonly assets: readonly NoticeAsset[];
  readonly nowMs: number;
}

const providerName = (p: string): string => (TRACKER_SOURCE_NAME as Readonly<Record<string, string>>)[p] ?? p;

/**
 * The SOS, lost-tracking and resumed events of one poll of one account (03-038; the cloud counterpart of Teslyn
 * Field's queue, 03-037, so a closed laptop does not stop an alert):
 * - SOS when the provider says SOS and the stored state did not; its key holds the stored state's time (so a poll
 *   retried after a failed store gives the same key).
 * - Lost tracking for every device whose `lost_since` is set (the poller's `trackerState` rule); its key holds that
 *   last fix's time, so every poll of the episode gives the same key and the database keeps one.
 * - Tracking resumed when a device that was lost reports a newer fix; it follows the lost-tracking event, so it is
 *   raised only after one was.
 * - Nothing for a device whose tracker asset is hidden, and nothing for an episode that started more than 2 hours
 *   ago (decision 445).
 */
export function trackerNotices(input: TrackerNoticeInput): NoticeEvent[] {
  const now = input.nowMs;
  const name = providerName(input.provider);
  const prev = new Map(input.previous.map((p) => [p.device_id, p]));
  const fixOf = new Map<string, NoticeFix>();
  for (const f of input.fixes) {
    const t = Date.parse(f.fix_time);
    if (!Number.isFinite(t)) continue;
    const had = fixOf.get(f.source_id);
    if (!had) fixOf.set(f.source_id, f);
    else {
      const ht = Date.parse(had.fix_time);
      // the newest fix, keeping a position or a label an older row had
      const [newer, older] = t >= ht ? [f, had] : [had, f];
      fixOf.set(f.source_id, {
        ...newer,
        label: newer.label || older.label || null,
        ...(newer.lat === undefined || newer.lat === null
          ? { lat: older.lat ?? null, lon: older.lon ?? null }
          : {}),
        ...(newer.alt_ft === undefined || newer.alt_ft === null ? { alt_ft: older.alt_ft ?? null } : {}),
      });
    }
  }
  const assets = input.assets.filter(
    (a) => !a.deleted_at && a.provider === input.provider && a.account_label === input.accountLabel,
  );
  const out: NoticeEvent[] = [];
  for (const a of input.alerts) {
    const device = a.source_id;
    const asset = assets.find((x) => x.device_id === device) ?? null;
    if (asset?.hidden) continue;
    const fix = fixOf.get(device) ?? null;
    const fixMs = fix ? Date.parse(fix.fix_time) : NaN;
    const label = asset?.label || fix?.label || asset?.registration || device;
    const where =
      fix && typeof fix.lat === 'number' && typeof fix.lon === 'number'
        ? `Last position ${noticePosition(fix.lat, fix.lon, fix.alt_ft)} at ${noticeTime(fixMs)}.`
        : Number.isFinite(fixMs)
          ? `Last position at ${noticeTime(fixMs)}.`
          : '';
    const keyBase = `${input.provider}:${input.accountLabel}:${device}`;
    const p = prev.get(device) ?? null;
    const detail = (extra: Record<string, string | number | boolean | null>) => ({
      provider: input.provider,
      account_label: input.accountLabel,
      device_id: device,
      label,
      last_fix_at: Number.isFinite(fixMs) ? new Date(fixMs).toISOString() : null,
      lat: fix && typeof fix.lat === 'number' ? fix.lat : null,
      lon: fix && typeof fix.lon === 'number' ? fix.lon : null,
      alt_ft: fix && typeof fix.alt_ft === 'number' ? Math.round(fix.alt_ft) : null,
      ...extra,
    });

    // SOS: starts when the provider says so and the stored state did not
    if (a.sos_active === true && !(p?.sos_active ?? false)) {
      const fresh = !Number.isFinite(fixMs) || now - fixMs <= ALERT_QUEUE_MAX_AGE_MS;
      if (fresh) {
        const since = p ? Date.parse(p.updated_at) : NaN;
        out.push({
          event_key: `sos:${keyBase}:${Number.isFinite(since) ? since : 'first'}`,
          kind: 'sos',
          title: clip(`SOS: ${label}`, NOTICE_TITLE_MAX),
          body: clip(`${name} reports SOS for ${label}. ${where}`.trim(), NOTICE_BODY_MAX),
          occurred_at: new Date(now).toISOString(),
          project_id: null,
          asset_id: asset?.id ?? a.asset_id,
          follows_key: null,
          detail: detail({}),
        });
      }
    }

    // resumed: a device that was lost reports a newer fix
    const prevLostMs = p?.lost_since ? Date.parse(p.lost_since) : NaN;
    const lostMs = a.lost_since ? Date.parse(a.lost_since) : NaN;
    if (
      Number.isFinite(prevLostMs) &&
      lostMs !== prevLostMs &&
      Number.isFinite(fixMs) &&
      fixMs > prevLostMs
    ) {
      const quietMin = Math.round((fixMs - prevLostMs) / 60_000);
      out.push({
        event_key: `tracking_resumed:${keyBase}:${prevLostMs}`,
        kind: 'tracking_resumed',
        title: clip(`Tracking resumed: ${label}`, NOTICE_TITLE_MAX),
        body: clip(
          `${label} is reporting again on ${name} after ${quietMin} min without a position. ${where}`.trim(),
          NOTICE_BODY_MAX,
        ),
        occurred_at: new Date(fixMs).toISOString(),
        project_id: null,
        asset_id: asset?.id ?? a.asset_id,
        follows_key: `lost_tracking:${keyBase}:${prevLostMs}`,
        detail: detail({ quiet_minutes: quietMin }),
      });
    }

    // lost tracking: every poll of the episode gives the same key
    if (Number.isFinite(lostMs) && now - lostMs <= ALERT_QUEUE_MAX_AGE_MS) {
      const minutes = Math.round((now - lostMs) / 60_000);
      out.push({
        event_key: `lost_tracking:${keyBase}:${lostMs}`,
        kind: 'lost_tracking',
        title: clip(`Lost tracking: ${label}`, NOTICE_TITLE_MAX),
        body: clip(
          `${label} was airborne and has sent no position for ${minutes} min (${name}). ${where}`.trim(),
          NOTICE_BODY_MAX,
        ),
        occurred_at: new Date(lostMs).toISOString(),
        project_id: null,
        asset_id: asset?.id ?? a.asset_id,
        follows_key: null,
        detail: detail({ minutes }),
      });
    }
  }
  return out;
}

// ------------------------------------------------------------------------------------------------ flights (F27)

/** A stored or new fix of a device for the flight notices (`tracker_position`, this poll's rows). */
export interface NoticeTrailRow {
  readonly source_id: string;
  readonly fix_time: string;
  readonly speed_kt: number | null;
  readonly lat?: number | null;
  readonly lon?: number | null;
  readonly alt_ft?: number | null;
  /** This poll's rows only: the tracker asset, label, registration and what the provider says the device is. */
  readonly asset_id?: string | null;
  readonly label?: string | null;
  readonly registration?: string | null;
  readonly kind?: 'fixed_wing' | 'helicopter' | 'ground' | null;
}

export interface FlightNoticeInput {
  readonly provider: string;
  readonly accountLabel: string;
  /** This poll's position rows: the devices looked at. */
  readonly rows: readonly NoticeTrailRow[];
  /** The stored trail of those devices (the last 12 hours, `tracker_position`); may overlap `rows`. */
  readonly trails: readonly NoticeTrailRow[];
  /** The tenant's tracker assets (with `kind` when known). */
  readonly assets: readonly (NoticeAsset & { readonly kind?: string | null })[];
  readonly nowMs: number;
}

/** A take-off or landing older than this is not raised (a catch-up of the tracker's history, a late poll). */
export const FLIGHT_NOTICE_MAX_AGE_MS = 30 * 60_000;
/** A landing with no newer fix is taken as final after this long (the tracker switched off on the ground). */
export const FLIGHT_LANDED_QUIET_MS = 10 * 60_000;

type FlightKind = 'fixed_wing' | 'helicopter' | 'ground';
const flightKind = (k: string | null | undefined): FlightKind =>
  k === 'helicopter' || k === 'ground' ? k : 'fixed_wing';

/**
 * Flight started and flight ended of one poll of one account (F27; docs/specs/mobile/teslyn-mobile.md §6.3). The
 * domain's `flightStatus` over each polled device's stored trail and new fixes:
 * - flight started: a take-off seen (not an approximate one, first seen airborne) within the last 30 minutes;
 * - flight ended: a landing within the last 30 minutes after which the aircraft stayed below the take-off speed for
 *   the speed-dip window (2 minutes: a slow fix 2 minutes on, or no fix for 10 minutes), so a dip is no landing.
 * The key holds the take-off time, so every poll of the flight gives the same key and the database keeps one event.
 * Never for a ground vehicle or a hidden tracker asset.
 */
export function flightNotices(input: FlightNoticeInput): NoticeEvent[] {
  const now = input.nowMs;
  const name = providerName(input.provider);
  const assets = input.assets.filter(
    (a) => !a.deleted_at && a.provider === input.provider && a.account_label === input.accountLabel,
  );
  const devices = new Map<string, NoticeTrailRow[]>();
  for (const r of input.rows) devices.set(r.source_id, [...(devices.get(r.source_id) ?? []), r]);
  const out: NoticeEvent[] = [];
  for (const [device, polled] of devices) {
    const asset = assets.find((a) => a.device_id === device) ?? null;
    if (asset?.hidden) continue;
    const newest = polled.reduce((a, b) => (Date.parse(b.fix_time) > Date.parse(a.fix_time) ? b : a));
    const kind = flightKind(newest.kind ?? asset?.kind ?? null);
    if (kind === 'ground') continue;
    const byTime = new Map<number, NoticeTrailRow>();
    for (const r of [...input.trails.filter((t) => t.source_id === device), ...polled]) {
      const t = Date.parse(r.fix_time);
      if (Number.isFinite(t)) byTime.set(t, r);
    }
    const points = [...byTime].map(([timeMs, r]) => ({
      lat: r.lat ?? 0,
      lon: r.lon ?? 0,
      timeMs,
      altFt: r.alt_ft ?? null,
      speedKt: r.speed_kt,
      headingDeg: null,
    }));
    const st = flightStatus(points, kind, now);
    if (!st) continue;
    const label = asset?.registration || asset?.label || newest.registration || newest.label || device;
    const deviceKey = asset?.id ?? `${input.provider}:${input.accountLabel}:${device}`;
    const assetId = asset?.id ?? newest.asset_id ?? null;
    const detail = {
      provider: input.provider,
      account_label: input.accountLabel,
      device_id: device,
      label,
      takeoff_at: new Date(st.takeoffMs).toISOString(),
      approximate: st.approximate,
      landing_at: st.landingMs === null ? null : new Date(st.landingMs).toISOString(),
      hours: Math.round(st.hours * 10) / 10,
    };
    if (!st.approximate && now - st.takeoffMs <= FLIGHT_NOTICE_MAX_AGE_MS)
      out.push({
        event_key: clip(`flight_started:${deviceKey}:${st.takeoffMs}`, 300),
        kind: 'flight_started',
        title: clip(`Flight started: ${label}`, NOTICE_TITLE_MAX),
        body: clip(`Took off ${noticeTime(st.takeoffMs)} (${name}).`, NOTICE_BODY_MAX),
        occurred_at: new Date(st.takeoffMs).toISOString(),
        project_id: null,
        asset_id: assetId,
        follows_key: null,
        detail,
      });
    if (st.landingMs !== null && now - st.landingMs <= FLIGHT_NOTICE_MAX_AGE_MS) {
      const lastMs = Math.max(...points.map((p) => p.timeMs));
      const stayed = lastMs - st.landingMs >= SPEED_DIP_MS || now - lastMs >= FLIGHT_LANDED_QUIET_MS;
      if (stayed)
        out.push({
          event_key: clip(`flight_ended:${deviceKey}:${st.takeoffMs}`, 300),
          kind: 'flight_ended',
          title: clip(`Flight ended: ${label}`, NOTICE_TITLE_MAX),
          body: clip(
            `Landed ${noticeTime(st.landingMs)} after ${st.approximate ? 'about ' : ''}${st.hours.toFixed(1)} h (${name}).`,
            NOTICE_BODY_MAX,
          ),
          occurred_at: new Date(st.landingMs).toISOString(),
          project_id: null,
          asset_id: assetId,
          follows_key: null,
          detail,
        });
    }
  }
  return out;
}

// ------------------------------------------------------------------------------------------------ flight watch (R180, R181)

/** R180, decision 1000: a flight with a planned time not seen to land this long after its landing ETA is overdue. */
export const FLIGHT_OVERDUE_GRACE_MS = 15 * 60_000;
/** R181: the operator's push comes this long before the landing ETA; a time shorter than this has none. */
export const FLIGHT_LANDING_SOON_MS = 30 * 60_000;
/**
 * Decision 1000: with no planned flight time, the aircraft's endurance less this reserve is when it must be down,
 * unless the aircraft register holds the aircraft's own reserve minutes (decision 132, `aircraft.reserve_min`).
 */
export const FLIGHT_RESERVE_DEFAULT_MIN = 45;

/** A tracker of the flight's aircraft, as `flight_watch_candidates` reads its stored fixes (`tracker_position`). */
export interface FlightWatchTracker {
  readonly asset_id: string;
  readonly label: string;
  /**
   * The newest stored fix (UTC ISO) and the poller's judgement of it: airborne or on the ground by the rules lost
   * tracking uses (the trackers' status words, the landing evidence of R167 and decision 949, then speed). Null: no
   * fix stored (positions are kept 48 hours).
   */
  readonly last_fix_at: string | null;
  readonly last_airborne: boolean | null;
  /** The newest stored fix judged airborne (UTC ISO), or null. */
  readonly last_airborne_at: string | null;
}

/**
 * The aircraft register's figures the watch needs (decision 506): the maximum endurance (`max_flight_h`, else TJR's
 * `max_flight_hrs`, else usable fuel ÷ fuel flow: `maxFlightHours`) and the aircraft's own reserve minutes.
 */
export interface FlightWatchAircraft {
  readonly max_flight_h: number | null;
  readonly fuel_capacity_l: number | null;
  readonly fuel_flow_lph: number | null;
  readonly reserve_min: number | null;
  readonly legacy_json: Readonly<Record<string, unknown>> | null;
}

/** An overdue alert of the flight that is not resolved yet. */
export interface FlightWatchOpen {
  readonly id: string;
  readonly event_key: string;
  /** Already made critical at the aircraft's maximum endurance (`flight_watch_escalate`). */
  readonly escalated?: boolean;
}

/** A flight entry with a take-off time, of the last 2 days (`flight_watch_candidates`). */
export interface FlightWatchEntry {
  readonly tenant_id: string;
  readonly id: string;
  readonly project_id: string;
  /** The project's number ("F26048"), for the words. */
  readonly project_number: string;
  /** The project's overrides: its UTC offset (`projectUtcOffsetH`), the clock the take-off time is typed in. */
  readonly qc_overrides: Readonly<Record<string, unknown>> | null;
  /** YYYY-MM-DD, the flight's day in project time. */
  readonly date: string;
  /** HH:MM in project time. */
  readonly takeoff_time: string | null;
  readonly planned_flight_h: number | null;
  /** The flight time typed on the phone after landing. */
  readonly flight_time_h: number | null;
  /** The laptop's split of the hours: more than 0 in all is a flight finished in Teslyn Field (`phoneFlightState`). */
  readonly svy_hours: number;
  readonly cbx_hours: number;
  readonly cal_hours: number;
  readonly fry_hours: number;
  readonly ext_hours: number;
  readonly flt_num: number | null;
  readonly entry_type: string;
  readonly deleted_at: string | null;
  /** The aircraft's registration ('' when the entry names none). */
  readonly registration: string;
  /** The aircraft's register figures; null or absent when the entry names no aircraft. */
  readonly aircraft?: FlightWatchAircraft | null;
  /**
   * The take-off time (HH:MM) of the aircraft's next flight entry that day, if there is one: once it has taken off,
   * this flight has landed, whatever the tracker says now (VH-JOZ flew FLT013 and FLT014 on 17 Oct 2026, 37 minutes
   * apart). Null or absent: none.
   */
  readonly next_takeoff_time?: string | null;
  /** The trackers of the aircraft (linked to it, or with its registration), hidden ones left out (decision 737). */
  readonly trackers: readonly FlightWatchTracker[];
  readonly open: readonly FlightWatchOpen[];
}

export interface FlightWatchRaise {
  readonly tenant_id: string;
  readonly event: NoticeEvent;
}

/** An overdue alert Teslyn resolves (`flight_watch_resolve`), with the reason in words. */
export interface FlightWatchResolve {
  readonly tenant_id: string;
  readonly id: string;
  readonly note: string;
}

/**
 * An overdue alert Teslyn makes critical (`flight_watch_escalate`, decision 1000): the open one (`id`), or the one
 * raised in the same run (`id` null: found by its `event_key` among the events `alert_raise` returns). Its title and
 * body are replaced and it is sent again, once, as critical.
 */
export interface FlightWatchEscalate {
  readonly tenant_id: string;
  readonly id: string | null;
  readonly event_key: string;
  readonly title: string;
  readonly body: string;
}

export interface FlightWatchResult {
  readonly raise: readonly FlightWatchRaise[];
  readonly resolve: readonly FlightWatchResolve[];
  readonly escalate: readonly FlightWatchEscalate[];
}

export interface FlightWatchTimes {
  /**
   * `planned`: the planned flight time typed on the phone (decision 986's landing ETA). `endurance`: none typed, so
   * the aircraft's maximum endurance less its reserve (decision 1000).
   */
  readonly basis: 'planned' | 'endurance';
  readonly takeoffMs: number;
  /** When it should be down: take-off plus the planned time, or plus the endurance less the reserve. */
  readonly etaMs: number;
  /** When it is overdue: the ETA plus `FLIGHT_OVERDUE_GRACE_MS` with a planned time; the ETA itself without one. */
  readonly dueMs: number;
  /** When "Landing in 30 minutes" is sent: 30 minutes before the ETA; null when the time is under 30 minutes. */
  readonly soonMs: number | null;
  /**
   * When an overdue flight becomes critical: take-off plus the maximum endurance, and never before it is overdue (a
   * plan longer than the endurance, ferry tanks, decision 144: critical as soon as it is overdue). Null with no
   * endurance in the register.
   */
  readonly criticalMs: number | null;
  readonly enduranceH: number | null;
  readonly reserveMin: number;
  readonly offsetH: number;
}

/** The aircraft's maximum endurance (h) and reserve (min) from the register, or null with no endurance. */
export function flightWatchEndurance(
  a: FlightWatchAircraft | null | undefined,
): { readonly hours: number; readonly reserveMin: number } | null {
  if (!a) return null;
  const hours = maxFlightHours({
    max_flight_h: a.max_flight_h,
    fuel_capacity_l: a.fuel_capacity_l,
    fuel_flow_lph: a.fuel_flow_lph,
    legacy_json: { ...(a.legacy_json ?? {}) },
  });
  if (hours === null || !(hours > 0)) return null;
  const r = a.reserve_min;
  const reserveMin =
    typeof r === 'number' && Number.isFinite(r) && r >= 0 && r <= 240 ? r : FLIGHT_RESERVE_DEFAULT_MIN;
  return { hours, reserveMin };
}

/**
 * A flight's watch times as instants (UTC ms), from the entry's day and take-off time in the project's time
 * (`projectUtcOffsetH`, as the crew type it on the phone and the laptop):
 * - with a planned flight time (decision 986's "Landing ETA", `phoneLanding`): the ETA is the take-off plus the
 *   planned time, overdue 15 minutes after it;
 * - with none (decision 1000): the ETA is the take-off plus the aircraft's maximum endurance less its reserve (45
 *   minutes unless the register holds the aircraft's own), overdue at once at it;
 * - either way the overdue flight becomes critical at the take-off plus the maximum endurance.
 * Null without a take-off time, or with neither a planned time nor an endurance in the register.
 */
export function flightWatchTimes(
  e: Pick<FlightWatchEntry, 'date' | 'takeoff_time' | 'planned_flight_h' | 'qc_overrides' | 'aircraft'>,
): FlightWatchTimes | null {
  const min = parseTime(e.takeoff_time);
  const day = Date.parse(`${e.date}T00:00:00Z`);
  if (min === null || !Number.isFinite(day)) return null;
  const offsetH = projectUtcOffsetH({ qc_overrides: e.qc_overrides });
  const takeoffMs = day + min * 60_000 - Math.round(offsetH * 3_600_000);
  const endurance = flightWatchEndurance(e.aircraft);
  const maxMs = endurance === null ? null : takeoffMs + Math.round(endurance.hours * 3_600_000);
  const base = {
    takeoffMs,
    offsetH,
    enduranceH: endurance?.hours ?? null,
    reserveMin: endurance?.reserveMin ?? FLIGHT_RESERVE_DEFAULT_MIN,
  };
  const soon = (etaMs: number): number | null =>
    etaMs - takeoffMs >= FLIGHT_LANDING_SOON_MS ? etaMs - FLIGHT_LANDING_SOON_MS : null;
  const planned = e.planned_flight_h;
  if (planned !== null && planned !== undefined && planned > 0) {
    const etaMs = takeoffMs + Math.round(planned * 3_600_000);
    const dueMs = etaMs + FLIGHT_OVERDUE_GRACE_MS;
    return {
      ...base,
      basis: 'planned',
      etaMs,
      dueMs,
      soonMs: soon(etaMs),
      criticalMs: maxMs === null ? null : Math.max(maxMs, dueMs),
    };
  }
  if (endurance === null || maxMs === null) return null;
  const etaMs = maxMs - Math.round(endurance.reserveMin * 60_000);
  if (!(etaMs > takeoffMs)) return null;
  return { ...base, basis: 'endurance', etaMs, dueMs: etaMs, soonMs: soon(etaMs), criticalMs: maxMs };
}

export interface FlightWatchLanding {
  /**
   * Finished in Teslyn Field, a flight time typed on the phone, the aircraft's next flight entry taken off, or the
   * aircraft's tracker on the ground.
   */
  readonly by: 'finished' | 'flight_time' | 'next_flight' | 'tracker';
  readonly atMs: number | null;
  readonly tracker: string | null;
}

/**
 * Whether a flight has been seen to land, by the evidence lost tracking and the landing alerts use: the flight
 * entry finished in Teslyn Field (its hours split) or given its flight time on the phone (`phoneFlightState`), or a
 * tracker of its aircraft judged on the ground at its newest stored fix after a fix judged airborne since the
 * take-off (the poller's judgement: TracPlus and V2Track landing words, the landing evidence of R167, then speed). A
 * tracker that has not reported the aircraft airborne since the take-off says nothing (it may be switched off), and
 * one airborne again after a stop says it is still flying (a fuel stop on a ferry, as F26048's ferry of 9 Oct 2026
 * by Forrest and Leonora), unless the aircraft's next flight entry has taken off by then (`next_takeoff_time`).
 */
export function flightWatchLanding(
  e: FlightWatchEntry,
  t: Pick<FlightWatchTimes, 'takeoffMs' | 'offsetH'>,
  nowMs: number,
): FlightWatchLanding | null {
  const takeoffMs = t.takeoffMs;
  if (e.svy_hours + e.cbx_hours + e.cal_hours + e.fry_hours + e.ext_hours > 0)
    return { by: 'finished', atMs: null, tracker: null };
  if (e.flight_time_h !== null && e.flight_time_h !== undefined && e.flight_time_h > 0)
    return { by: 'flight_time', atMs: takeoffMs + Math.round(e.flight_time_h * 3_600_000), tracker: null };
  const next = parseTime(e.next_takeoff_time ?? null);
  if (next !== null) {
    const nextMs = Date.parse(`${e.date}T00:00:00Z`) + next * 60_000 - Math.round(t.offsetH * 3_600_000);
    if (nextMs > takeoffMs && nextMs <= nowMs) return { by: 'next_flight', atMs: nextMs, tracker: null };
  }
  for (const x of e.trackers) {
    const last = x.last_fix_at ? Date.parse(x.last_fix_at) : NaN;
    const up = x.last_airborne_at ? Date.parse(x.last_airborne_at) : NaN;
    if (x.last_airborne === false && up >= takeoffMs && last > up)
      return { by: 'tracker', atMs: last, tracker: x.label };
  }
  return null;
}

/** "10:06", or "00:30 (next day)" after the flight's own day, in project time. */
function projectClock(ms: number, offsetH: number, date: string): string {
  const local = new Date(utcToLocalMs(ms, offsetH)).toISOString();
  return `${local.slice(11, 16)}${local.slice(0, 10) > date ? ' (next day)' : ''}`;
}

const utcClock = (ms: number): string => `${new Date(ms).toISOString().slice(11, 16)} UTC`;

/** "4.5 h", or "3 h 20 min" when the time is not a whole tenth of an hour. */
function hoursWords(h: number): string {
  const m = Math.round(h * 60);
  return m % 6 === 0 ? `${(m / 60).toFixed(1)} h` : `${Math.floor(m / 60)} h ${m % 60} min`;
}

/** "VH-JOZ Flt 013": the aircraft and the flight number, when the entry has them. */
function flightName(e: FlightWatchEntry): string {
  const who = e.registration || 'A flight';
  return e.flt_num === null ? who : `${who} Flt ${String(e.flt_num).padStart(3, '0')}`;
}

/** What the aircraft's trackers last said, for an overdue flight's words. */
function trackerWords(e: FlightWatchEntry, t: FlightWatchTimes): string {
  if (e.trackers.length === 0) return 'No tracker is linked to the aircraft.';
  const since = e.trackers.find((x) => x.last_fix_at && Date.parse(x.last_fix_at) >= t.takeoffMs);
  const x = since ?? e.trackers[0]!;
  if (!since) return `Its tracker (${x.label}) has sent no position since take-off.`;
  const at = projectClock(Date.parse(x.last_fix_at!), t.offsetH, e.date);
  if (x.last_airborne) return `Its tracker (${x.label}) last reported it airborne at ${at} project time.`;
  return `Its tracker (${x.label}) has not reported it airborne since take-off (last position ${at} project time).`;
}

function landingNote(l: FlightWatchLanding, e: FlightWatchEntry, offsetH: number): string {
  if (l.by === 'finished') return 'Landed: the flight was finished in Teslyn Field.';
  if (l.by === 'flight_time') return `Landed: the flight time (${hoursWords(e.flight_time_h!)}) was entered.`;
  if (l.by === 'next_flight')
    return `Landed: the aircraft’s next flight took off at ${projectClock(l.atMs!, offsetH, e.date)} project time.`;
  return `Landed: its tracker (${l.tracker}) reported it on the ground at ${projectClock(l.atMs!, offsetH, e.date)} project time.`;
}

const NOT_DOWN = 'No flight time on the phone and not finished in Teslyn Field.';

/**
 * The flight watch of one run of the tracker poller (R180, R181, decision 1000; docs/specs/cloud/alerts.md §3.1),
 * over the flight entries with a take-off time and a planned flight time or an aircraft with an endurance in the
 * register (`flightWatchTimes`):
 * - "Landing in 30 minutes" (R181) once per flight (key `flight_landing_soon:<entry>`) from 30 minutes before the ETA
 *   until the ETA, while the flight has not landed; none for a time under 30 minutes, and none once the ETA has
 *   passed (a plan entered or first seen after it). A planned time changed before it is sent moves it; once sent it
 *   is not sent again.
 * - "Overdue flight" (R180) at `dueMs` while the flight has not been seen to land, once per ETA (key
 *   `flight_overdue:<entry>:<ETA ms>`); not raised when that moment is more than 2 hours past (decision 445's limit
 *   for lost tracking: an old entry never given its flight time is not news), unless the flight reached its maximum
 *   endurance within the last 2 hours.
 * - At the maximum endurance (`criticalMs`) the overdue alert becomes critical, once (`escalate`): within 2 hours of
 *   that moment, for the open alert of the current ETA not yet escalated, or the one raised in this run.
 * - Teslyn resolves an open overdue alert when the landing is seen (`flightWatchLanding`), when the entry is removed
 *   or has no ETA any more, and when the ETA changes (a new planned time; the new ETA is alerted in its turn).
 */
export function flightWatchNotices(input: {
  readonly entries: readonly FlightWatchEntry[];
  readonly nowMs: number;
}): FlightWatchResult {
  const now = input.nowMs;
  const raise: FlightWatchRaise[] = [];
  const resolve: FlightWatchResolve[] = [];
  const escalate: FlightWatchEscalate[] = [];
  for (const e of input.entries) {
    const close = (note: string, keep: string | null = null): void => {
      for (const o of e.open)
        if (o.event_key !== keep) resolve.push({ tenant_id: e.tenant_id, id: o.id, note: clip(note, 300) });
    };
    if (e.deleted_at || e.entry_type !== 'flight') {
      close('The flight entry was removed.');
      continue;
    }
    const t = flightWatchTimes(e);
    if (!t) {
      close('The flight has no take-off time, planned flight time or aircraft endurance now.');
      continue;
    }
    const landing = flightWatchLanding(e, t, now);
    if (landing) {
      close(landingNote(landing, e, t.offsetH));
      continue;
    }
    const eta = projectClock(t.etaMs, t.offsetH, e.date);
    const name = flightName(e);
    const where = e.project_number ? `${name} (${e.project_number})` : name;
    const endurance = t.enduranceH === null ? '' : hoursWords(t.enduranceH);
    const plan =
      t.basis === 'planned'
        ? `take-off ${e.takeoff_time} plus the planned ${hoursWords(e.planned_flight_h!)}`
        : `take-off ${e.takeoff_time} plus its endurance of ${endurance} less the ${t.reserveMin} min reserve`;
    const critical =
      t.criticalMs === null ? null : `${projectClock(t.criticalMs, t.offsetH, e.date)} project time`;
    const assetId = e.trackers[0]?.asset_id ?? null;
    const detail = {
      entry_id: e.id,
      flt_num: e.flt_num,
      registration: e.registration,
      basis: t.basis,
      takeoff_at: new Date(t.takeoffMs).toISOString(),
      eta_at: new Date(t.etaMs).toISOString(),
      planned_h: e.planned_flight_h,
      endurance_h: t.enduranceH,
      reserve_min: t.basis === 'endurance' ? t.reserveMin : null,
      critical_at: t.criticalMs === null ? null : new Date(t.criticalMs).toISOString(),
      offset_h: t.offsetH,
    };
    const overdueKey = clip(`flight_overdue:${e.id}:${t.etaMs}`, 300);
    close(`The planned flight time was changed: landing ETA now ${eta} project time.`, overdueKey);
    const atCritical =
      t.criticalMs !== null && now >= t.criticalMs && now - t.criticalMs <= ALERT_QUEUE_MAX_AGE_MS;
    const overdue = now >= t.dueMs && (now - t.dueMs <= ALERT_QUEUE_MAX_AGE_MS || atCritical);
    if (overdue) {
      const due =
        t.basis === 'planned'
          ? `its landing ETA was ${eta} project time (${utcClock(t.etaMs)}), ${plan}.`
          : `with no planned flight time it was due down by ${eta} project time (${utcClock(t.etaMs)}), ${plan}.`;
      const reaches =
        critical && now < t.criticalMs! ? ` It reaches its maximum endurance at ${critical}.` : '';
      raise.push({
        tenant_id: e.tenant_id,
        event: {
          event_key: overdueKey,
          kind: 'flight_overdue',
          title: clip(`Overdue flight: ${name}`, NOTICE_TITLE_MAX),
          body: clip(
            `${where} has not been seen to land: ${due}${reaches} ${trackerWords(e, t)} ${NOT_DOWN}`,
            NOTICE_BODY_MAX,
          ),
          occurred_at: new Date(t.dueMs).toISOString(),
          project_id: e.project_id,
          asset_id: assetId,
          follows_key: null,
          detail,
        },
      });
    }
    // decision 1000: still not down at the maximum endurance: critical, once
    const open = e.open.find((o) => o.event_key === overdueKey) ?? null;
    if (overdue && atCritical && !open?.escalated)
      escalate.push({
        tenant_id: e.tenant_id,
        id: open?.id ?? null,
        event_key: overdueKey,
        title: clip(`Overdue flight at maximum endurance: ${name}`, NOTICE_TITLE_MAX),
        body: clip(
          `${where} has still not been seen to land and reached its maximum endurance (${endurance}) at ${critical} (${utcClock(t.criticalMs!)}), take-off ${e.takeoff_time}. ${trackerWords(e, t)} ${NOT_DOWN}`,
          NOTICE_BODY_MAX,
        ),
      });
    if (t.soonMs !== null && now >= t.soonMs && now < t.etaMs) {
      const minutes = Math.max(1, Math.round((t.etaMs - now) / 60_000));
      const body =
        t.basis === 'planned'
          ? `${where} is due to land at ${eta} project time (${utcClock(t.etaMs)}), in ${minutes} min: ${plan}.`
          : `${where} has no planned flight time and is due down by ${eta} project time (${utcClock(t.etaMs)}), in ${minutes} min: ${plan}.`;
      raise.push({
        tenant_id: e.tenant_id,
        event: {
          event_key: clip(`flight_landing_soon:${e.id}`, 300),
          kind: 'flight_landing_soon',
          title: clip(`Landing in ${minutes} min: ${name}`, NOTICE_TITLE_MAX),
          body: clip(body, NOTICE_BODY_MAX),
          occurred_at: new Date(now).toISOString(),
          project_id: e.project_id,
          asset_id: assetId,
          follows_key: null,
          detail,
        },
      });
    }
  }
  return { raise, resolve, escalate };
}

/** The notice kind of each Diurnal QC alert kind (`diurnal-alerts.ts` AlertKind) that is sent (06-056, C3). */
export const DIURNAL_NOTICE_KIND: Readonly<Record<string, NoticeKindId>> = {
  exceedance: 'diurnal_exceedance',
  bad_data: 'diurnal_bad_data',
  gap: 'diurnal_gap',
  offline: 'diurnal_offline',
};

/** A Diurnal QC alert (`DiurnalAlert`) as the event cloud diurnal QC (C3) raises for its project; null: not sent. */
export function diurnalNotice(
  alert: {
    readonly key: string;
    readonly kind: string;
    readonly source_label: string;
    readonly at_ms: number;
    readonly title: string;
    readonly details: string;
  },
  projectId: string,
): NoticeEvent | null {
  const kind = DIURNAL_NOTICE_KIND[alert.kind];
  if (!kind || !Number.isFinite(alert.at_ms)) return null;
  return {
    event_key: clip(`${kind}:${projectId}:${alert.key}`, 300),
    kind,
    title: clip(`${noticeKind(kind)!.label}: ${alert.title}`, NOTICE_TITLE_MAX),
    body: clip(`${alert.details} (${alert.source_label}, ${noticeTime(alert.at_ms)})`, NOTICE_BODY_MAX),
    occurred_at: new Date(alert.at_ms).toISOString(),
    project_id: projectId,
    asset_id: null,
    follows_key: null,
    detail: { source: alert.source_label },
  };
}

// ------------------------------------------------------------------------------------------------ recipients

/** A staff member of the organisation (`membership`), with their sign-in email. */
export interface NoticeMember {
  readonly user_id: string;
  readonly roles: readonly string[];
  readonly email: string | null;
  /** The staff register lists them as a pilot (`person.roles`); used by kinds that never go to pilots. */
  readonly pilot?: boolean;
}

/** A project crew row (`project_crew`) with the sign-in of its register person (`person.user_id`). */
export interface NoticeCrewRow {
  readonly project_id: string;
  readonly user_id: string | null;
  readonly role: string;
  readonly start_date: string | null;
  readonly end_date: string | null;
  readonly deleted_at?: string | null;
}

/** An assignment of the event's tracker asset to a project (`tracker_assignment`). */
export interface NoticeAssignment {
  readonly project_id: string;
  readonly starts_at: string | null;
  readonly ends_at: string | null;
  readonly deleted_at?: string | null;
}

/** A person's choice for one kind (`alert_preference`): null keeps the default. */
export interface NoticePreference {
  readonly user_id: string;
  readonly kind: string;
  readonly push: boolean | null;
  readonly email: boolean | null;
}

export interface NoticeAudienceInput {
  readonly event: {
    readonly kind: string;
    readonly project_id: string | null;
    readonly occurred_at: string;
  };
  readonly members: readonly NoticeMember[];
  readonly crew: readonly NoticeCrewRow[];
  readonly assignments: readonly NoticeAssignment[];
  readonly preferences: readonly NoticePreference[];
  /**
   * The sign-ins of the event's flight's operators (`alert_audience` `flight_operators`: the flight entry's operator
   * slots), for the kinds with `flightOperators`. Absent or empty: none named.
   */
  readonly flightOperators?: readonly string[];
  /** The one sign-in the event names, for the kinds with `toUser` (`alert_audience` `to_user`). */
  readonly toUser?: string | null;
}

export interface NoticeRecipient {
  readonly user_id: string;
  readonly email: string | null;
  readonly push: boolean;
  readonly sendEmail: boolean;
  /**
   * default: in the kind's default audience; chosen: switched on by the person; staging: on the staging-only list
   * (decision 975), whatever the rules and their choices say.
   */
  readonly reason: 'default' | 'chosen' | 'staging';
}

/** The staff roles that receive notices and keep alert choices; clients never (Q37). */
export const NOTICE_STAFF_ROLES: readonly NoticeTenantRole[] = [
  'tenant_admin',
  'manager',
  'field_crew',
  'office_staff',
  'data_processor',
];

export function isNoticeStaff(roles: readonly string[]): boolean {
  return roles.some((r) => (NOTICE_STAFF_ROLES as readonly string[]).includes(r));
}

/** The projects an event is about: its own, else the projects its aircraft is assigned to at the event's time. */
export function noticeProjects(
  event: NoticeAudienceInput['event'],
  assignments: readonly NoticeAssignment[],
): string[] {
  if (event.project_id) return [event.project_id];
  const t = Date.parse(event.occurred_at);
  const ids = new Set<string>();
  for (const a of assignments) {
    if (a.deleted_at) continue;
    if (a.starts_at && Date.parse(a.starts_at) > t) continue;
    if (a.ends_at && Date.parse(a.ends_at) < t) continue;
    ids.add(a.project_id);
  }
  return [...ids].sort();
}

/**
 * Who receives a notice when nobody on a crew and nobody in the kind's fallback roles would (decision 919: an SOS still
 * reaches someone in an organisation with no manager).
 */
export const NOTICE_LAST_RESORT: readonly NoticeTenantRole[] = ['tenant_admin'];

/**
 * Who receives an event and on which channels: the kind's crew roles on the crew of its projects on the event's day
 * (UTC date; a crew row that has ended is someone on break, decision 919), else the kind's staff roles (managers),
 * else the tenant admins; then each person's own choices (on for a person outside the audience, on break included,
 * off for one inside it). Staff of the organisation only.
 */
export function noticeRecipients(input: NoticeAudienceInput): NoticeRecipient[] {
  const kind = noticeKind(input.event.kind);
  if (!kind) return [];
  const staff = new Map(input.members.filter((m) => isNoticeStaff(m.roles)).map((m) => [m.user_id, m]));
  const projects = new Set(noticeProjects(input.event, input.assignments));
  const day = new Date(Date.parse(input.event.occurred_at)).toISOString().slice(0, 10);
  const audience = new Set<string>();
  // R180, R181: the flight's own operators in place of the crew's, when the flight names any with a sign-in
  const named = kind.flightOperators ? (input.flightOperators ?? []).filter((u) => staff.has(u)) : [];
  const roles = (kind.crewRoles as readonly string[]).filter((r) => named.length === 0 || r !== 'operator');
  for (const u of named) audience.add(u);
  // R191: the one person the event names (the staff member who sent the request), if they are staff
  if (kind.toUser && input.toUser && staff.has(input.toUser)) audience.add(input.toUser);
  // each person's roles on the crew of the event's projects that day
  const crewRoles = new Map<string, Set<string>>();
  for (const c of crewOnDate(input.crew, day)) {
    if (!projects.has(c.project_id) || !c.user_id || !staff.has(c.user_id)) continue;
    crewRoles.set(c.user_id, (crewRoles.get(c.user_id) ?? new Set()).add(c.role));
    if (roles.includes(c.role)) audience.add(c.user_id);
  }
  // decision 811: a kind that never goes to pilots leaves out the crew's pilots and, off the crew, the register's
  const pilot = (userId: string): boolean => {
    if (!kind.neverPilots) return false;
    const onCrew = crewRoles.get(userId);
    return onCrew ? onCrew.has('pilot') : Boolean(staff.get(userId)?.pilot);
  };
  for (const u of [...audience]) if (pilot(u)) audience.delete(u);
  // R180: the organisation's managers when none is on the crew that day
  const crewManager = [...crewRoles.values()].some((r) => r.has('manager'));
  if (kind.managersWithoutCrew && !crewManager)
    for (const m of staff.values())
      if (m.roles.includes('manager') && !pilot(m.user_id)) audience.add(m.user_id);
  // nobody on the crew that day: the kind's staff roles (managers), else the tenant admins; never the field crew
  // role alone, who may be at home on break (decision 919)
  for (const fallback of kind.noLastResort
    ? [kind.fallbackRoles]
    : [kind.fallbackRoles, NOTICE_LAST_RESORT]) {
    if (audience.size > 0) break;
    for (const m of staff.values())
      if (m.roles.some((r) => (fallback as readonly string[]).includes(r)) && !pilot(m.user_id))
        audience.add(m.user_id);
  }
  const prefs = new Map(input.preferences.filter((p) => p.kind === kind.id).map((p) => [p.user_id, p]));
  const out: NoticeRecipient[] = [];
  for (const m of [...staff.values()].sort((a, b) => a.user_id.localeCompare(b.user_id))) {
    if (pilot(m.user_id)) continue;
    const inAudience = audience.has(m.user_id);
    const p = prefs.get(m.user_id);
    const push = p?.push ?? (inAudience && kind.defaults.push);
    const email = (p?.email ?? (inAudience && kind.defaults.email)) && Boolean(m.email);
    if (!push && !email) continue;
    out.push({
      user_id: m.user_id,
      email: m.email,
      push,
      sendEmail: email,
      reason: inAudience ? 'default' : 'chosen',
    });
  }
  return out;
}

// ------------------------------------------------------------------------------------------------ staging only

/** The environment name that alone allows the staging-only switch (decision 975, go-live plan GL-21). */
export const STAGING_ENVIRONMENT = 'staging';
/** The word in front of every notice's title while the switch is on. */
export const STAGING_NOTICE_PREFIX = 'Staging: ';

/**
 * The staging-only switch (decision 975, go-live plan GL-21): once production is live, staging keeps polling
 * Thomson's real trackers and stations, so without it each alert would reach the crews twice. The notify Edge
 * Function reads two of its secrets, `TESLYN_ENVIRONMENT` and `TESLYN_STAGING_ALERTS_TO` (sign-in emails, separated
 * by commas, semicolons or spaces), and this gives the list of people every notice of that environment goes to
 * instead of the recipients rules, lower-cased and without repeats; null means normal delivery. It is null whenever
 * the environment is not exactly "staging" (production's is "production" or unset, so the list alone can never
 * switch production) or the list holds no address (the environment alone never silences staging).
 */
export function stagingAlertsTo(env: {
  readonly environment?: string | null;
  readonly to?: string | null;
}): readonly string[] | null {
  if ((env.environment ?? '').trim().toLowerCase() !== STAGING_ENVIRONMENT) return null;
  const list = [
    ...new Set(
      (env.to ?? '')
        .split(/[\s,;]+/)
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.includes('@')),
    ),
  ];
  return list.length > 0 ? list : null;
}

/** A notice's title while the switch is on: "Staging: Lost tracking: VH-JEI" (once, however often it is applied). */
export function stagingNoticeTitle(title: string): string {
  return title.startsWith(STAGING_NOTICE_PREFIX) ? title : `${STAGING_NOTICE_PREFIX}${title}`;
}

/**
 * Who receives an event while the switch is on: the listed people who are staff of the organisation, by push (every
 * device of theirs) and by email, whatever the crew, the kind's defaults and their own choices say. A listed person
 * who is not a member of the organisation receives nothing, and nobody else ever does, so the event ends "none".
 */
export function stagingRecipients(
  members: readonly NoticeMember[],
  to: readonly string[],
): NoticeRecipient[] {
  const wanted = new Set(to.map((e) => e.trim().toLowerCase()));
  return members
    .filter((m) => isNoticeStaff(m.roles) && m.email && wanted.has(m.email.trim().toLowerCase()))
    .sort((a, b) => a.user_id.localeCompare(b.user_id))
    .map((m) => ({ user_id: m.user_id, email: m.email, push: true, sendEmail: true, reason: 'staging' }));
}

// ------------------------------------------------------------------------------------------------ acknowledge, tests

/** Who may acknowledge an event (Diurnal inventory 02 §2.8: field crew, managers, data processors; tenant admins too, decision 736). */
export const NOTICE_ACK_ROLES: readonly NoticeTenantRole[] = [
  'tenant_admin',
  'manager',
  'field_crew',
  'data_processor',
];

export function canAcknowledgeNotice(roles: readonly string[]): boolean {
  return roles.some((r) => (NOTICE_ACK_ROLES as readonly string[]).includes(r));
}

// ------------------------------------------------------------------------------------------------ removing (R159)

/**
 * Who removes alerts from the history (R159, MB 2 Nov 2026: "Tenant admins can delete items from the alert
 * history"). A removed alert is hidden from everyone, never deleted: its event key repeats on every poll while the
 * condition lasts, so a deleted alert would be raised and sent again (`alert_remove`, 20261102150000).
 */
export const NOTICE_REMOVE_ROLES: readonly NoticeTenantRole[] = ['tenant_admin'];

/** Kinds that must be acknowledged or resolved before they are removed: an emergency is never removed unseen. */
export const NOTICE_REMOVE_AFTER_ACK: readonly NoticeKindId[] = ['sos', 'lost_tracking'];

export function canRemoveNotices(roles: readonly string[]): boolean {
  return roles.some((r) => (NOTICE_REMOVE_ROLES as readonly string[]).includes(r));
}

/** Why this event may not be removed yet, in words, or null when it may. */
export function noticeRemoveProblem(e: NoticeAckFields & { readonly kind: string }): string | null {
  if ((NOTICE_REMOVE_AFTER_ACK as readonly string[]).includes(e.kind) && noticeNeedsAcknowledging(e))
    return 'Acknowledge this alert before removing it: an SOS or lost-tracking alert is never removed unseen.';
  return null;
}

/** The question "Remove" asks before it acts. */
export function removeNoticeQuestion(title: string): string {
  return `Remove “${title}” from the alert history? Nobody in the organisation will see it again, here, in Teslyn Field or on the phone. It is not sent again, and the removal is in the audit log.`;
}

// ------------------------------------------------------------------------------------------------ device names (R158)

/** The longest name a person gives a browser or phone that receives their alerts. */
export const DEVICE_NAME_MAX = 60;

/** A device name as stored: spaces trimmed and runs of them made one; null for none (the automatic label shows). */
export function cleanDeviceName(name: string | null | undefined): string | null {
  const v = (name ?? '').replace(/\s+/g, ' ').trim();
  return v ? v : null;
}

/** What is wrong with a name typed for a device, or null. */
export function deviceNameProblem(name: string | null | undefined): string | null {
  const v = cleanDeviceName(name);
  if (v !== null && v.length > DEVICE_NAME_MAX)
    return `A device name is at most ${DEVICE_NAME_MAX} characters.`;
  return null;
}

/** A device's name as the Alerts page shows it: the person's own name for it, else its automatic label. */
export function deviceShownName(d: {
  readonly kind: 'browser' | 'phone';
  readonly name?: string | null;
  readonly label?: string | null;
}): string {
  return (
    cleanDeviceName(d.name) ?? cleanDeviceName(d.label) ?? (d.kind === 'phone' ? 'A phone' : 'A browser')
  );
}

/**
 * What a phone's line on the Alerts page says about push (MB, 4 Nov 2026): it receives push alerts; the person turned
 * alerts off in Teslyn Mobile on it (`push_off`); its push address expired (FCM or APNs said the token is gone); or
 * it has no token because alerts were never turned on in the app or the phone blocks notifications.
 */
export function phonePushWords(p: {
  readonly push_provider: string | null;
  readonly push_expired_at: string | null;
  readonly push_off?: boolean | null;
}): string {
  if (p.push_off) return 'alerts turned off in Teslyn Mobile on it';
  if (!p.push_provider)
    return 'alerts not turned on in Teslyn Mobile on it, or notifications not allowed on the phone';
  if (p.push_expired_at) return 'push address expired: open Teslyn Mobile on it';
  return 'receives push alerts';
}

/** The question before a phone is removed from "Your devices" (MB, 4 Nov 2026). */
export function phoneRemoveQuestion(shown: string): string {
  return `Remove “${shown}” from your devices? It stops receiving your push alerts. If Teslyn Mobile is still signed in on it, the phone lists itself again the next time the app opens there: to keep it off, turn off alerts on the app’s Alerts tab, or sign out on it.`;
}

/** What the page says once a phone is removed. */
export function phoneRemovedWords(shown: string): string {
  return `${shown} removed: it no longer receives your push alerts.`;
}

// ------------------------------------------------------------------------------------------------ the stored history (02-015)

/**
 * The stored alert history (Diurnal parity C3, 02-015; inventory 02 §2.8): each event is open until someone
 * acknowledges it ("I have seen it"), may be snoozed (no notice of the same kind about the same station or aircraft on
 * the same project until the snooze ends; the events raised meanwhile are kept as "snoozed", never sent), and is
 * resolved when the problem is over (with a note; resolving acknowledges too). The same people as acknowledge
 * (`NOTICE_ACK_ROLES`). SOS is never snoozed: every SOS is sent.
 */
export type NoticeState = 'open' | 'acknowledged' | 'snoozed' | 'resolved';

export const NOTICE_STATE_LABEL: Readonly<Record<NoticeState, string>> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  snoozed: 'Snoozed',
  resolved: 'Resolved',
};

export interface NoticeHistoryFields {
  readonly kind: string;
  readonly acknowledged_at: string | null;
  readonly snoozed_until: string | null;
  readonly resolved_at: string | null;
}

/** An event's state at a time: resolved, else snoozed while its snooze lasts, else acknowledged, else open. */
export function noticeState(e: NoticeHistoryFields, nowMs: number): NoticeState {
  if (e.resolved_at) return 'resolved';
  if (e.snoozed_until && Date.parse(e.snoozed_until) > nowMs) return 'snoozed';
  return e.acknowledged_at ? 'acknowledged' : 'open';
}

/** The snoozes offered, in minutes (`alert_snooze` takes 1 to 1,440). */
export const NOTICE_SNOOZE_CHOICES: readonly { readonly minutes: number; readonly label: string }[] = [
  { minutes: 60, label: '1 hour' },
  { minutes: 240, label: '4 hours' },
  { minutes: 720, label: '12 hours' },
];

/** Kinds that are never snoozed: every SOS is sent. */
export const NOTICE_NEVER_SNOOZED: readonly NoticeKindId[] = ['sos'];

export function canSnoozeNotice(kind: string): boolean {
  return !(NOTICE_NEVER_SNOOZED as readonly string[]).includes(kind);
}

/**
 * Kinds that are acknowledged and never resolved (`ackOnly`: flight started and ended, R140; tracking resumed,
 * decision 941; magnetic exceedances, MB 3 Nov 2026): acknowledging is their final state, so no Resolve is offered
 * and the resolve action is never sent for them. An event of such a kind resolved before it became acknowledge-only
 * stays resolved; nothing reopens it.
 */
export function canResolveNotice(kind: string): boolean {
  return !noticeKind(kind)?.ackOnly;
}

/**
 * Whether Teslyn itself resolved this event (`resolvedByTeslyn` kinds: an overdue flight once its landing is seen,
 * R180): resolved with nobody as the resolver. "Resolved by Teslyn" on the Alerts page; the note says why.
 */
export function noticeResolvedByTeslyn(e: {
  readonly kind: string;
  readonly resolved_at?: string | null;
  readonly resolved_by?: string | null;
}): boolean {
  return Boolean(e.resolved_at) && !e.resolved_by && Boolean(noticeKind(e.kind)?.resolvedByTeslyn);
}

/** The history's filters on the Alerts page and in Teslyn Field. */
export type NoticeHistoryFilter = 'needs_action' | 'acknowledged' | 'all' | 'resolved';

/**
 * Whether an event shows under a filter. "Needs action" is everything not resolved, less the acknowledge-only kinds
 * once acknowledged (R140: nothing is left to do). "Acknowledged" (R141) is what was acknowledged and not resolved:
 * every acknowledged flight start, flight end, tracking resumed and magnetic exceedance, and any other alert seen
 * but not yet resolved. A snooze is not an acknowledgement.
 */
export function noticeInFilter(e: NoticeHistoryFields, filter: NoticeHistoryFilter, nowMs: number): boolean {
  const s = noticeState(e, nowMs);
  if (filter === 'all') return true;
  if (filter === 'resolved') return s === 'resolved';
  if (filter === 'acknowledged') return s === 'acknowledged';
  return s !== 'resolved' && !(s === 'acknowledged' && !canResolveNotice(e.kind));
}

/** An event's fields that say whether it waits to be acknowledged. */
export interface NoticeAckFields {
  readonly acknowledged_at: string | null;
  /** Missing on rows read before the history's columns existed: not resolved. */
  readonly resolved_at?: string | null;
}

/**
 * Whether an event still waits for someone to say they have seen it (R122, MB 27 Oct 2026): not acknowledged and not
 * resolved (resolving acknowledges too). A snooze does not acknowledge: a snoozed event still asks to be acknowledged.
 * The acknowledge-only kinds (`ackOnly`: flight started, flight ended, tracking resumed, magnetic exceedances) wait
 * like the rest.
 */
export function noticeNeedsAcknowledging(e: NoticeAckFields & { readonly kind?: string }): boolean {
  return !e.acknowledged_at && !e.resolved_at;
}

/**
 * Kinds "Acknowledge all" leaves out (decision 835, Q746 C): each SOS and each lost-tracking alert keeps its own
 * "Acknowledge" tap, so an emergency is never acknowledged along with a pile of other notices.
 */
export const NOTICE_ACK_ONE_BY_ONE: readonly NoticeKindId[] = ['sos', 'lost_tracking'];

/** Whether "Acknowledge all" acknowledges this event: it waits to be acknowledged and is not SOS or lost tracking. */
export function noticeInAcknowledgeAll(e: NoticeAckFields & { readonly kind: string }): boolean {
  return noticeNeedsAcknowledging(e) && !(NOTICE_ACK_ONE_BY_ONE as readonly string[]).includes(e.kind);
}

/** The events "Acknowledge all" acknowledges, in the order given. */
export function noticesToAcknowledgeAll<T extends NoticeAckFields & { readonly kind: string }>(
  rows: readonly T[],
): T[] {
  return rows.filter(noticeInAcknowledgeAll);
}

/**
 * The question "Acknowledge all" asks before it acts (decision 835): how many it acknowledges, and how many SOS and
 * lost-tracking alerts it leaves to be acknowledged one by one.
 */
export function acknowledgeAllQuestion(count: number, leftOut: number): string {
  const what = `Acknowledge ${count} ${count === 1 ? 'alert' : 'alerts'}? Each is marked as seen by you.`;
  if (leftOut === 0) return what;
  return leftOut === 1
    ? `${what} 1 SOS or lost-tracking alert is left for its own Acknowledge.`
    : `${what} ${leftOut} SOS or lost-tracking alerts are left for their own Acknowledge.`;
}

/**
 * The history's order on the Alerts page (R122: "the alert history at the top, so staff are reminded to acknowledge
 * alerts"): the events not yet acknowledged first, then the others; newest first within each. A new array; events
 * with the same time keep their order.
 */
export function noticeHistoryOrder<T extends NoticeAckFields & { readonly occurred_at: string }>(
  rows: readonly T[],
): T[] {
  return rows
    .map((r, i) => ({ r, i, wait: noticeNeedsAcknowledging(r) ? 0 : 1, t: Date.parse(r.occurred_at) }))
    .sort((a, b) => a.wait - b.wait || b.t - a.t || a.i - b.i)
    .map((x) => x.r);
}

/** One CSV cell: quoted when needed; a leading = + - @ is written as text so a spreadsheet never runs it. */
function csvCell(s: string | null | undefined): string {
  let v = s ?? '';
  if (/^[=+\-@]/.test(v)) v = `'${v}`;
  return /[",\n\r']/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** The history as CSV (inventory 02 §3.9: the alert log is replaced by the stored history and a CSV export). */
export function noticeHistoryCsv(
  rows: readonly (NoticeHistoryFields & {
    readonly title: string;
    readonly body: string;
    readonly occurred_at: string;
    readonly acknowledged_by_name?: string | null;
    readonly resolved_by_name?: string | null;
    readonly resolved_note?: string | null;
  })[],
  nowMs: number,
): string {
  const head = [
    'Time (UTC)',
    'Kind',
    'Title',
    'Details',
    'State',
    'Acknowledged at (UTC)',
    'Acknowledged by',
    'Snoozed until (UTC)',
    'Resolved at (UTC)',
    'Resolved by',
    'Note',
  ];
  const lines = [head.join(',')];
  for (const r of rows) {
    lines.push(
      [
        r.occurred_at,
        noticeKind(r.kind)?.label ?? r.kind,
        r.title,
        r.body,
        NOTICE_STATE_LABEL[noticeState(r, nowMs)],
        r.acknowledged_at,
        r.acknowledged_by_name,
        r.snoozed_until,
        r.resolved_at,
        r.resolved_by_name,
        r.resolved_note,
      ]
        .map(csvCell)
        .join(','),
    );
  }
  return `${lines.join('\r\n')}\r\n`;
}

/** Test notices a person may send in an hour (each test press sends to every device of theirs, or one email). */
export const NOTICE_TESTS_PER_HOUR = 10;

export function noticeTestAllowed(sentInLastHour: number): boolean {
  return sentInLastHour < NOTICE_TESTS_PER_HOUR;
}

/** "Sent to 2 devices, 1 failed, 1 expired" / "No device has push turned on": a test's result in words. */
export function noticeTestWords(r: {
  readonly sent: number;
  readonly failed: number;
  readonly expired: number;
  readonly channel: NoticeChannel;
}): string {
  if (r.channel === 'email') return r.sent > 0 ? 'Test email sent.' : 'The test email could not be sent.';
  if (r.sent + r.failed + r.expired === 0)
    return 'No device has push turned on for you. Turn on push on this device first.';
  const parts = [`Sent to ${r.sent} device${r.sent === 1 ? '' : 's'}`];
  if (r.failed > 0) parts.push(`${r.failed} failed`);
  if (r.expired > 0) parts.push(`${r.expired} expired and removed`);
  return `${parts.join(', ')}.`;
}

// ------------------------------------------------------------------------------------------------ delivery

/** What a push service's answer means (RFC 8030 §5, §7.3): 404 and 410 mean the subscription is gone. */
export function pushOutcome(status: number): 'sent' | 'expired' | 'failed' {
  if (status >= 200 && status < 300) return 'sent';
  if (status === 404 || status === 410) return 'expired';
  return 'failed';
}

/** The JSON a push notice carries to the service worker (`apps/web/public/teslyn-sw.js`); under 4 KB. */
export interface NoticePushPayload {
  readonly v: 1;
  readonly kind: string;
  readonly level: NoticeLevel;
  readonly title: string;
  readonly body: string;
  /** When it happened (ms), shown by the phone beside the notice. */
  readonly at: number;
  /** One notice per event on a device (a repeat replaces it). */
  readonly tag: string;
  /** Opened when the notice is pressed. */
  readonly url: string;
  /** Where the notice's Acknowledge button posts its token; null for a test. */
  readonly ack: { readonly url: string; readonly token: string } | null;
  readonly test: boolean;
}

export function noticePushPayload(
  e: {
    readonly id: string;
    readonly kind: string;
    readonly title: string;
    readonly body: string;
    readonly occurred_at: string;
    /** The event's own level when it was raised above its kind's (an overdue flight at maximum endurance). */
    readonly level?: string | null;
  },
  opts: { readonly openUrl: string; readonly ack: NoticePushPayload['ack']; readonly test: boolean },
): NoticePushPayload {
  const at = Date.parse(e.occurred_at);
  return {
    v: 1,
    kind: e.kind,
    level: noticeEventLevel(e),
    title: clip(e.title, NOTICE_TITLE_MAX),
    body: clip(e.body, NOTICE_BODY_MAX),
    at: Number.isFinite(at) ? at : 0,
    tag: `teslyn:${e.id}`,
    url: opts.openUrl,
    ack: opts.ack,
    test: opts.test,
  };
}

const LEVELS: readonly NoticeLevel[] = ['normal', 'urgent', 'critical'];

/**
 * An event's level: its kind's, or the event's own when it was raised above it (`alert_event.level`: an overdue
 * flight still airborne at its aircraft's maximum endurance is critical, decision 1000). Never below the kind's.
 */
export function noticeEventLevel(e: { readonly kind: string; readonly level?: string | null }): NoticeLevel {
  const own = noticeKind(e.kind)?.level ?? 'normal';
  const raised = LEVELS.indexOf(e.level as NoticeLevel);
  return raised > LEVELS.indexOf(own) ? LEVELS[raised]! : own;
}

/** Android notification channel of a native push notice (Teslyn Mobile creates the three). */
export type NoticeNativeChannel = 'critical' | 'alerts' | 'flights';

/**
 * A native push notice to a phone (Teslyn Mobile, FCM on Android and APNs on iPhone; docs/specs/mobile/teslyn-mobile.md
 * §6): the words, the data the app opens the alert with, the Android channel (the kind's own `nativeChannel`,
 * flights for flight start and end and tracking resumed; else critical for the critical kinds, alerts for the rest:
 * an acknowledge-only QC warning such as a magnetic exceedance stays on alerts), the priority (the kind's urgency),
 * how long it is worth delivering and the iPhone interruption level (time-sensitive for critical and urgent kinds).
 */
export interface NoticeNativeMessage {
  readonly title: string;
  readonly body: string;
  /** String values only (FCM's `data`). */
  readonly data: {
    readonly event_id: string;
    readonly kind: string;
    readonly level: NoticeLevel;
    readonly at: string;
    readonly test: '0' | '1';
  };
  readonly channel: NoticeNativeChannel;
  readonly priority: 'high' | 'normal';
  readonly ttlSeconds: number;
  readonly interruption: 'time-sensitive' | 'active';
  /** One notice per event on a phone (a repeat replaces it). */
  readonly tag: string;
}

export function noticeNativeMessage(
  e: {
    readonly id: string;
    readonly kind: string;
    readonly title: string;
    readonly body: string;
    readonly occurred_at: string;
    /** The event's own level when it was raised above its kind's (an overdue flight at maximum endurance). */
    readonly level?: string | null;
  },
  opts: { readonly test: boolean },
): NoticeNativeMessage {
  const kind = noticeKind(e.kind);
  const level = noticeEventLevel(e);
  const at = Date.parse(e.occurred_at);
  return {
    title: clip(e.title, NOTICE_TITLE_MAX),
    body: clip(e.body, NOTICE_BODY_MAX),
    data: {
      event_id: e.id,
      kind: e.kind,
      level,
      at: String(Number.isFinite(at) ? at : 0),
      test: opts.test ? '1' : '0',
    },
    channel: kind?.nativeChannel ?? (level === 'critical' ? 'critical' : 'alerts'),
    priority: kind?.urgency === 'high' ? 'high' : 'normal',
    ttlSeconds: kind?.ttlSeconds ?? 3600,
    interruption: level === 'normal' ? 'active' : 'time-sensitive',
    tag: `teslyn:${e.id}`,
  };
}

/** The Alerts page of a Teslyn Cloud address, opened at one event (or the page for a test). */
export function noticeOpenUrl(origin: string, eventId: string | null): string {
  const base = origin.replace(/\/+$/, '');
  return eventId ? `${base}/?page=alerts&alert=${encodeURIComponent(eventId)}` : `${base}/?page=alerts`;
}

/**
 * The sender of an alert email (decision 890, replacing 624's alerts@teslyn.au): Teslyn's own sender named after the
 * organisation, "No Reply Thomson Airborne powered by Teslyn" <no-reply@teslyn.au>.
 */
export function noticeEmailFrom(tenantName: string | null | undefined): string {
  return appEmailFrom(tenantName);
}

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export interface NoticeEmail {
  readonly subject: string;
  readonly text: string;
  readonly html: string;
}

/** A title without the staging-only mark (decision 975). */
const bareTitle = (title: string): string =>
  title.startsWith(STAGING_NOTICE_PREFIX) ? title.slice(STAGING_NOTICE_PREFIX.length) : title;

/** The email of one event: plain text and the same words as escaped HTML. */
export function noticeEmail(
  e: { readonly kind: string; readonly title: string; readonly body: string; readonly occurred_at: string },
  opts: { readonly tenantName: string; readonly openUrl: string | null; readonly test: boolean },
): NoticeEmail {
  const kind = noticeKind(e.kind);
  const at = Date.parse(e.occurred_at);
  const when = Number.isFinite(at) ? noticeTime(at) : '';
  const lines = [
    e.body,
    '',
    when ? `When: ${when}` : '',
    kind ? `Alert: ${kind.label}` : '',
    opts.openUrl
      ? `Open the alert and acknowledge it: ${opts.openUrl}`
      : 'Open Teslyn Cloud, Alerts, to acknowledge it.',
    '',
    `You receive this because of your alert choices in ${opts.tenantName}'s Teslyn Cloud (Alerts). Change them there.`,
  ].filter((l, i, all) => l !== '' || (i > 0 && all[i - 1] !== ''));
  const text = lines.join('\n');
  const html = [
    `<p>${escapeHtml(e.body)}</p>`,
    when ? `<p>When: ${escapeHtml(when)}<br>` : '<p>',
    kind ? `Alert: ${escapeHtml(kind.label)}</p>` : '</p>',
    opts.openUrl
      ? `<p><a href="${escapeHtml(opts.openUrl)}">Open the alert and acknowledge it</a></p>`
      : '<p>Open Teslyn Cloud, Alerts, to acknowledge it.</p>',
    `<p><small>You receive this because of your alert choices in ${escapeHtml(opts.tenantName)}'s Teslyn Cloud (Alerts). Change them there.</small></p>`,
  ].join('\n');
  return {
    // a test says so once, behind the staging mark when there is one (decision 975): "Staging: Test: SOS: VH-TST"
    subject: clip(opts.test && !bareTitle(e.title).startsWith('Test') ? `Test: ${e.title}` : e.title, 150),
    text,
    html,
  };
}
